import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Grid3X3, Calculator, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useApi, useAuth } from '@/contexts/AuthContext';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A' };

function MatrixGrid({ matrix }) {
  const dims = matrix.dimensions;
  const labels = d => (d.type === 'enum' ? d.values : d.bands.map(b => b.label));
  if (dims.length === 1) {
    return (
      <table className="w-full text-xs" data-testid="matrix-grid">
        <thead><tr><th className="text-left py-1 pr-2" style={{ color: C.slate }}>{dims[0].label}</th><th className="text-left py-1" style={{ color: C.slate }}>Rate</th></tr></thead>
        <tbody>{labels(dims[0]).map(l => (
          <tr key={l} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.1)' }}>
            <td className="py-1 pr-2 font-medium" style={{ color: C.navy }}>{l}</td>
            <td className="py-1 font-mono">{matrix.cells[l] != null ? `$${Number(matrix.cells[l]).toLocaleString()}` : <span style={{ color: C.amber }}>REFER</span>}</td>
          </tr>))}</tbody>
      </table>
    );
  }
  const rows = labels(dims[0]);
  const cols = labels(dims[1]);
  const extras = dims.slice(2);
  const combos = extras.reduce((acc, d) => acc.flatMap(c => labels(d).map(l => [...c, l])), [[]]);
  return (
    <div className="space-y-3" data-testid="matrix-grid">
      {combos.map(extra => (
        <div key={extra.join('|') || 'single'}>
          {extra.length > 0 && (
            <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: C.blue }}>
              {extras.map((d, i) => `${d.label}: ${extra[i]}`).join(' · ')}
            </p>
          )}
          <table className="w-full text-xs">
            <thead><tr>
              <th className="text-left py-1 pr-2" style={{ color: C.slate }}>{dims[0].label} \ {dims[1].label}</th>
              {cols.map(cl => <th key={cl} className="text-right py-1 px-2" style={{ color: C.slate }}>{cl}</th>)}
            </tr></thead>
            <tbody>
              {rows.map(rl => (
                <tr key={rl} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.1)' }}>
                  <td className="py-1 pr-2 font-medium" style={{ color: C.navy }}>{rl}</td>
                  {cols.map(cl => {
                    const key = [rl, cl, ...extra].join('|');
                    const v = matrix.cells[key];
                    return <td key={cl} className="py-1 px-2 text-right font-mono" data-testid={`matrix-cell-${key}`}>
                      {v != null ? `$${Number(v).toLocaleString()}` : <span className="font-sans text-[10px] font-semibold" style={{ color: C.amber }}>REFER</span>}
                    </td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function QuoteTester({ api, matrix }) {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const set = (k, v) => setAnswers(p => ({ ...p, [k]: v }));
  const run = async () => {
    try {
      const r = await api.post(`/matrix-rating/matrices/${matrix.matrix_id}/quote`, { answers });
      setResult(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Quote failed'); }
  };
  return (
    <div className="p-3 rounded-sm space-y-2" style={{ background: 'rgba(26,76,124,0.05)' }} data-testid="matrix-quote-tester">
      <p className="text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1.5" style={{ color: C.navy }}>
        <Calculator className="w-3.5 h-3.5" /> Quote tester — the premium is found, not computed
      </p>
      <div className="flex flex-wrap gap-2 items-center">
        {matrix.dimensions.map(d => d.type === 'enum' ? (
          <select key={d.key} value={answers[d.key] || ''} onChange={e => set(d.key, e.target.value)}
            data-testid={`matrix-answer-${d.key}`} className="px-2 py-1 border border-input rounded-sm text-[11px] bg-white">
            <option value="">{d.label}…</option>
            {d.values.map(v => <option key={v} value={v} label={String(v)} />)}
          </select>
        ) : (
          <input key={d.key} type="number" value={answers[d.key] || ''} onChange={e => set(d.key, e.target.value)}
            placeholder={d.label} data-testid={`matrix-answer-${d.key}`}
            className="px-2 py-1 border border-input rounded-sm text-[11px] bg-white w-32" />
        ))}
        {matrix.cell_basis === 'rate_per_1000' && (
          <input type="number" value={answers[matrix.exposure_field] || ''} onChange={e => set(matrix.exposure_field, e.target.value)}
            placeholder={`${matrix.exposure_field} ($)`} data-testid="matrix-answer-exposure"
            className="px-2 py-1 border border-input rounded-sm text-[11px] bg-white w-36" />
        )}
        {(matrix.loadings || []).map(ld => (
          <label key={ld.key} className="flex items-center gap-1 text-[11px]" style={{ color: C.navy }}>
            <input type="checkbox" checked={!!answers[ld.key]} onChange={e => set(ld.key, e.target.checked)}
              data-testid={`matrix-loading-${ld.key}`} />
            {ld.label} (×{ld.factor})
          </label>
        ))}
        <button onClick={run} data-testid="matrix-quote-btn"
          className="px-3 py-1 rounded-sm text-[11px] font-medium text-white" style={{ background: C.navy }}>Rate it</button>
      </div>
      {result && (
        <div className="p-2.5 rounded-sm" data-testid="matrix-quote-result"
          style={{ background: result.premium != null ? 'rgba(29,158,117,0.08)' : 'rgba(232,160,32,0.1)' }}>
          {result.premium != null ? (
            <p className="text-xs" style={{ color: C.navy }}>
              <strong style={{ color: C.green }}>${result.premium.toLocaleString()}</strong>
              {' '}· cell <span className="font-mono">{result.cell_key}</span> = ${Number(result.cell_value).toLocaleString()}
              {result.loadings_applied.map(l => ` × ${l.factor} (${l.label})`).join('')}
              {result.minimum_applied ? ' · minimum premium applied' : ''}
              {' '}· matrix v{result.matrix_version}
            </p>
          ) : (
            (result.refer_flags || []).map((f, i) => <p key={i} className="text-[11px] font-medium" style={{ color: '#A8720E' }}>REFER — {f}</p>)
          )}
        </div>
      )}
    </div>
  );
}

function CreateMatrixForm({ api, onCreated }) {
  const TEMPLATE = JSON.stringify({
    name: 'My Scheme Matrix', scheme_class: 'bundled_scheme',
    dimensions: [
      { key: 'size_band', label: 'Size', type: 'range', bands: [{ label: 'small', min: 0, max: 100 }, { label: 'large', min: 100, max: null }] },
      { key: 'state', label: 'State', type: 'enum', values: ['NSW', 'VIC'] },
    ],
    cells: { 'small|NSW': 1000, 'small|VIC': 950, 'large|NSW': 2400, 'large|VIC': 2200 },
    cell_basis: 'flat_premium', loadings: [], minimum_premium: 500, notes: '',
  }, null, 2);
  const [json, setJson] = useState(TEMPLATE);
  const [open, setOpen] = useState(false);
  const submit = async () => {
    let body;
    try { body = JSON.parse(json); } catch { toast.error('Invalid JSON'); return; }
    try {
      const r = await api.post('/matrix-rating/matrices', body);
      toast.success(`Matrix '${r.data.name}' filed as v${r.data.version}`);
      setOpen(false); onCreated();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(Array.isArray(d?.validation_errors) ? d.validation_errors[0] : (typeof d === 'string' ? d : 'Failed'));
    }
  };
  if (!open) {
    return <button onClick={() => setOpen(true)} data-testid="matrix-create-toggle-btn"
      className="flex items-center gap-1 px-3 py-1.5 rounded-sm text-xs font-medium text-white" style={{ background: C.blue }}>
      <Plus className="w-3.5 h-3.5" /> File New Matrix
    </button>;
  }
  return (
    <div className="w-full space-y-2" data-testid="matrix-create-form">
      <textarea value={json} onChange={e => setJson(e.target.value)} rows={14} data-testid="matrix-create-json-input"
        className="w-full p-2 border border-input rounded-sm text-[11px] font-mono bg-white" />
      <div className="flex gap-2">
        <button onClick={submit} data-testid="matrix-create-submit-btn"
          className="px-4 py-1.5 rounded-sm text-xs font-medium text-white" style={{ background: C.navy }}>File Matrix v1.0</button>
        <button onClick={() => setOpen(false)} className="text-[11px] underline" style={{ color: C.slate }}>cancel</button>
      </div>
    </div>
  );
}

export default function MatrixRatingPage() {
  const api = useApi();
  const { user } = useAuth();
  const canManage = ['platform_owner', 'mga_admin'].includes(user?.role);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => api.get('/matrix-rating/matrices').then(r => {
    setData(r.data);
    setSelected(s => s || r.data.matrices.find(m => m.status === 'active') || r.data.matrices[0] || null);
  }).catch(() => {}), [api]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="text-xs py-6 text-center" style={{ color: C.slate }}>Loading scheme matrices…</p>;

  return (
    <div className="space-y-4" data-testid="matrix-rating-page">
      <Card className="p-4 rounded-md space-y-3" style={{ border: '1px solid rgba(26,76,124,0.2)' }}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2">
            <Grid3X3 className="w-4 h-4 mt-0.5" style={{ color: C.blue }} />
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Scheme Matrices — Matrix Lookup Rating</h3>
              <p className="text-[11px]" style={{ color: C.slate }}>
                The rating shape behind every scheme: answers resolve to bands, bands form a cell key, the cell holds the premium.
                No formulas, no fallbacks — an unfiled cell refers. Matrices are immutable; a revision files a new version.
              </p>
            </div>
          </div>
          {canManage && <CreateMatrixForm api={api} onCreated={load} />}
        </div>
        <div className="flex flex-wrap gap-2" data-testid="matrix-list">
          {data.matrices.map(m => (
            <button key={m.matrix_id} onClick={() => setSelected(m)} data-testid={`matrix-select-${m.matrix_id}`}
              className="px-2.5 py-1.5 rounded-sm text-[11px] font-medium text-left"
              style={{
                background: selected?.matrix_id === m.matrix_id ? 'rgba(26,76,124,0.1)' : 'rgba(13,27,42,0.04)',
                border: `1px solid ${selected?.matrix_id === m.matrix_id ? C.blue : 'transparent'}`, color: C.navy,
              }}>
              {m.name} <span className="font-mono" style={{ color: C.slate }}>v{m.version}</span>
              <span className="ml-1.5 px-1 py-0.5 rounded text-[9px] font-semibold uppercase"
                style={{
                  background: m.status === 'active' ? 'rgba(29,158,117,0.1)' : m.status === 'retired' ? 'rgba(226,75,74,0.1)' : 'rgba(232,160,32,0.12)',
                  color: m.status === 'active' ? C.green : m.status === 'retired' ? C.red : '#A8720E',
                }}>{m.status}</span>
            </button>
          ))}
          {data.matrices.length === 0 && <p className="text-xs" style={{ color: C.slate }} data-testid="matrix-list-empty">No matrices filed yet.</p>}
        </div>
      </Card>

      {selected && (
        <Card className="p-4 rounded-md space-y-3" style={{ border: '1px solid rgba(136,150,168,0.15)' }} data-testid="matrix-detail">
          <div>
            <h4 className="text-xs font-semibold" style={{ color: C.navy }}>{selected.name} · v{selected.version}
              <span className="ml-2 font-normal" style={{ color: C.slate }}>
                {selected.cell_basis === 'rate_per_1000' ? `rate per $1,000 of ${selected.exposure_field}` : 'flat premium'} · minimum ${Number(selected.minimum_premium || 0).toLocaleString()}
              </span>
            </h4>
            {selected.notes && <p className="text-[11px] mt-0.5" style={{ color: C.slate }}>{selected.notes}</p>}
          </div>
          <QuoteTester api={api} matrix={selected} />
          <MatrixGrid matrix={selected} />
        </Card>
      )}
    </div>
  );
}
